<template>
  <div class="card mb-4">
    <div class="card-header">
      <h5 class="fs-6 mb-0">
        Trend History
        <a href="https://www.youtube.com/watch?v=6G17MbjeX8c" target="_blank" title="Watch explainer video"><span class="fab fa-youtube" data-fa-transform="right-0 down-1"></span></a>
      </h5>
      <p class="mb-0"> 
        <span v-if="detector == 'gc'">This chart shows the % of assets in an Uptrend (Green) or Downtrend (Red).</span>
        <span v-if="detector == 'b2s'">This chart shows the % of assets in an Uptrend (Green), Downtrend (Red) or Undecided (Grey).</span>
      </p>
    </div>
    <div class="card-body">
      <Line ref="lineChart" :data="chartData" :options="chartOptions" />
    </div>
  </div>
</template> 

<script>
import { Chart as ChartJS, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler } from 'chart.js'
import { Line } from 'vue-chartjs'
ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler)

export default {
  name: 'TrendRadarChart',
  components: { Line },
  props: {
    data: { type: Array, required: true },
    detector: { type: String, default: 'gc' }
  },
  computed: {
    theme () { return this.$store.getters.getTheme },
    labelColor () { return this.theme === 'dark' ? 'rgb(157, 169, 187)' : 'rgb(94, 110, 130)' },
    gridColor () { return this.theme === 'dark' ? 'rgba(157, 169, 187, 0.15)' : 'rgba(94, 110, 130, 0.1)' },
    chartData () {
      const sorted = [...this.data].sort((a, b) => a.runDate.localeCompare(b.runDate))
      // Each row is normalised by its own `total`. Day-by-day universe-size
      // changes (top-100 -> top-200, partial fetch failures) don't distort
      // the chart shape — every point is a % share of that run's scan.
      // Clamp to [0, 100] defensively in case of stale/drifted data where
      // a category count exceeds the row's `total`.
      // (API may return numerics as strings from PHP-DBAL; `Number()` coerces.)
      const pct = (key) => sorted.map(d => {
        const total = Number(d.total) || (Number(d.green) + Number(d.grey) + Number(d.red))
        if (!(total > 0)) return 0
        return Math.max(0, Math.min(100, Number(d[key]) / total * 100))
      })
      // Each dataset carries its raw-count key so the tooltip can look up
      // the underlying integer without inferring from the (potentially
      // localized) human label.
      const datasets = [
        {
          label: 'Uptrend', _key: 'green', data: pct('green'),
          borderColor: '#28a745', backgroundColor: 'rgba(40, 167, 69, 0.1)',
          fill: true, tension: 0.3, pointStyle: 'circle', pointRadius: 3, pointHoverRadius: 6, borderWidth: 2
        }
      ]
      if (this.detector === 'b2s') {
        datasets.push({
          label: 'Undecided', _key: 'grey', data: pct('grey'),
          borderColor: '#6c757d', backgroundColor: 'rgba(108, 117, 125, 0.1)',
          fill: true, tension: 0.3, pointStyle: 'rectRot', pointRadius: 3, pointHoverRadius: 6, borderWidth: 2, borderDash: [2, 4]
        })
      }
      datasets.push({
        label: 'Downtrend', _key: 'red', data: pct('red'),
        borderColor: '#dc3545', backgroundColor: 'rgba(220, 53, 69, 0.1)',
        fill: true, tension: 0.3, pointStyle: 'rect', pointRadius: 3, pointHoverRadius: 6, borderWidth: 2, borderDash: [6, 4]
      })
      // Stash the raw counts on the chart-level object so the tooltip can
      // still surface "12 of 200" even though the lines are %-scaled.
      const rawCounts = sorted.map(d => ({
        green: Number(d.green) || 0,
        grey:  Number(d.grey)  || 0,
        red:   Number(d.red)   || 0,
        total: Number(d.total) || ((Number(d.green) || 0) + (Number(d.grey) || 0) + (Number(d.red) || 0)),
      }))
      return {
        labels: sorted.map(d => { const p = d.runDate.split('-'); return p[1] + '/' + p[2] }),
        datasets,
        _rawCounts: rawCounts,
      }
    },
    chartOptions () {
      const lc = this.labelColor, gc = this.gridColor
      return {
        responsive: true, maintainAspectRatio: true, aspectRatio: window.innerWidth < 768 ? 1.2 : 3, animation: false,
        plugins: {
          legend: { position: 'top', labels: { color: lc, usePointStyle: true, padding: 15 } },
          tooltip: { callbacks: { label: function (ctx) {
            const pctVal = Number(ctx.dataset.data[ctx.dataIndex]) || 0
            const pctStr = pctVal.toFixed(1) + '%'
            // Pull the matching raw count for this day. `_key` is stamped
            // onto each dataset above so we never have to infer from the
            // (potentially i18n-ed) human label.
            const raw = ctx.chart.data._rawCounts?.[ctx.dataIndex]
            const key = ctx.dataset._key
            if (!raw || !key) return `${ctx.dataset.label}: ${pctStr}`
            return `${ctx.dataset.label}: ${pctStr} (${raw[key]} of ${raw.total})`
          }}}
        },
        scales: {
          x: { ticks: { color: lc, maxRotation: 0, autoSkipPadding: 10 }, grid: { display: false } },
          y: {
            min: 0, max: 100,
            ticks: {
              color: lc,
              callback: function (val) { return val + '%' }
            },
            grid: { color: gc }
          }
        }
      }
    }
  },
  methods: {
    handleResize () {
      const chart = this.$refs.lineChart?.chart
      if (chart) {
        chart.options.aspectRatio = window.innerWidth < 768 ? 1.2 : 3
        chart.resize()
      }
    }
  },
  mounted () {
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>
